import { api } from '@/redux/apiSlice'
import TAGS from '@/redux/cacheTags'
import countryStates from '@/utils/enums/countryStates'
import { countriesCodeMap } from '@/utils/enums/countries'

const extendedApi = api.injectEndpoints({
    endpoints: (build) => ({
        getAllFulfillmentProviders: build.query({
            query: () => `fulfillment/`,
            providesTags: [TAGS.fulfillment],
            transformResponse: (providers) =>
                providers?.results.map((provider) => ({
                    ...provider,
                    icon: '',
                })),
        }),
        getFulfillmentProvider: build.query({
            query: ({ fulfillment_id }) => `fulfillment/${fulfillment_id}/`,
            providesTags: [TAGS.fulfillment],
            transformResponse: (provider) => ({
                ...provider,
                icon: '',
            }),
        }),
        createFulfillmentProvider: build.mutation({
            query: (data) => ({
                url: `fulfillment/`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [TAGS.fulfillment],
        }),
        updateFulfillmentProvider: build.mutation({
            query: ({ id, ...data }) => ({
                url: `fulfillment/${id}/`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: [TAGS.fulfillment],
        }),
        testUserCredentials: build.mutation({
            query: (data) => ({
                url: `fulfillment/test/`,
                method: 'POST',
                body: data,
            }),
        }),
        deleteFulfillmentProvider: build.mutation({
            query: ({ provider_id }) => ({
                url: `fulfillment/${provider_id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [TAGS.fulfillment],
        }),
        createFulfillmentProviderConfig: build.mutation({
            query: ({ provider_id, ...data }) => ({
                url: `fulfillment/${provider_id}/configurations/`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [TAGS.fulfillment],
        }),
        updateFulfillmentProviderConfig: build.mutation({
            query: ({ provider_id, configuration_id, ...data }) => ({
                url: `fulfillment/${provider_id}/configurations/${configuration_id}/`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: [TAGS.fulfillment],
        }),
        deleteFulfillmentProviderConfig: build.mutation({
            query: ({ provider_id, configuration_id }) => ({
                url: `fulfillment/${provider_id}/configurations/${configuration_id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [TAGS.fulfillment],
        }),

        getFulfillmentProviderShippingZones: build.query({
            query: () => `fulfillment/shipping-zones/`,
            providesTags: [TAGS.fulfillmentShippingZones],
            transformResponse: (zonesWithCountries) =>
                zonesWithCountries
                    .flatMap((zone) =>
                        zone.countries.map((country) => ({
                            ...country,
                            name: country.country_name,
                            code: country.country_code,
                            zone: zone.name,
                            states: countryStates[country.country_code] || [],
                            region: countriesCodeMap.get(country.country_code)?.region || '',
                        })),
                    )
                    .sort((a, b) => a.name.localeCompare(b.name)),
        }),

        getStoreList: build.query({
            query: ({ provider_type, ...params }) => ({
                url: `${provider_type}/stores/`,
                params,
            }),
            providesTags: [TAGS.emailProviderList],
        }),

        getFulfillmentCarriers: build.query({
            query: () => ({
                url: `fulfillment/carrier-mappings/`,
            }),
            providesTags: [TAGS.fulfillmentCarriers],
        }),
    }),
})
export const {
    useGetAllFulfillmentProvidersQuery,
    useGetFulfillmentProviderQuery,
    useCreateFulfillmentProviderMutation,
    useUpdateFulfillmentProviderMutation,
    useTestUserCredentialsMutation,
    useDeleteFulfillmentProviderMutation,
    useCreateFulfillmentProviderConfigMutation,
    useUpdateFulfillmentProviderConfigMutation,
    useDeleteFulfillmentProviderConfigMutation,

    useGetFulfillmentProviderShippingZonesQuery,
    useGetStoreListQuery,
    useLazyGetStoreListQuery,

    useGetFulfillmentCarriersQuery,
} = extendedApi
